import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  Shield, Zap, DollarSign, FileSearch, CheckCircle, XCircle,
  Activity, TrendingUp, RefreshCw, Lock, Loader2, Wifi, WifiOff,
  AlertTriangle, BarChart3
} from 'lucide-react';

const HealthDot = ({ status }) => (
  <span className={`inline-block w-2 h-2 rounded-full ${
    status === 'healthy' ? 'bg-green-500' : status === 'degraded' ? 'bg-amber-500' : 'bg-red-500'
  }`} />
);

export default function AIGovernancePage() {
  const [perf, setPerf] = useState(null);
  const [costs, setCosts] = useState(null);
  const [audit, setAudit] = useState(null);
  const [chainVerify, setChainVerify] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [raSync, setRaSync] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [perfR, costsR, auditR, healthR, raR] = await Promise.all([
        api.get('/governance/performance'),
        api.get('/governance/costs'),
        api.get('/governance/audit'),
        api.get('/governance/integration-health'),
        api.get('/governance/rightangle-sync'),
      ]);
      setPerf(perfR.data);
      setCosts(costsR.data);
      setAudit(auditR.data);
      setIntegrations(healthR.data);
      setRaSync(raR.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const verifyChain = async () => {
    setVerifying(true);
    try {
      const { data } = await api.get('/governance/audit/verify');
      setChainVerify(data);
    } catch (e) { console.error(e); }
    finally { setVerifying(false); }
  };

  if (loading) return (
    <div className="p-6 md:p-8 space-y-6">
      <Skeleton className="h-8 w-64" />
      <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-28" />)}</div>
      <Skeleton className="h-96" />
    </div>
  );

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="governance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-950">AI Governance</h1>
          <p className="text-sm text-slate-500 mt-1">Performance, costs, compliance & integration monitoring</p>
        </div>
        <div className="flex items-center gap-2">
          {integrations.map(i => (
            <Badge key={i.integration_name} variant="outline" className="rounded-sm text-[10px] flex items-center gap-1.5" data-testid={`health-${i.integration_name}`}>
              <HealthDot status={i.status} />
              {i.integration_name}
            </Badge>
          ))}
        </div>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="governance-kpis">
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Automation Rate</p>
            <p className="font-mono text-2xl font-bold text-[#16A34A] mt-1">{perf?.automation_rate}%</p>
            <Progress value={perf?.automation_rate} className="h-1 mt-2 rounded-sm" />
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Matching Accuracy</p>
            <p className="font-mono text-2xl font-bold text-[#007AFF] mt-1">{perf?.matching_accuracy}%</p>
            <Progress value={perf?.matching_accuracy} className="h-1 mt-2 rounded-sm" />
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Override Rate</p>
            <p className="font-mono text-2xl font-bold text-amber-600 mt-1">{perf?.override_rate}%</p>
            <p className="text-[10px] text-slate-400 mt-1">{perf?.total_attributions} total attributions</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">ROI Multiplier</p>
            <p className="font-mono text-2xl font-bold text-[#16A34A] mt-1">{costs?.roi_multiplier}x</p>
            <p className="text-[10px] text-slate-400 mt-1">${costs?.fte_cost_avoided?.toLocaleString()} saved</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="performance">
        <TabsList className="rounded-sm h-9 bg-slate-100 p-0.5">
          <TabsTrigger value="performance" className="rounded-sm text-xs" data-testid="tab-perf">
            <Activity className="h-3 w-3 mr-1" />Performance
          </TabsTrigger>
          <TabsTrigger value="costs" className="rounded-sm text-xs" data-testid="tab-costs">
            <DollarSign className="h-3 w-3 mr-1" />AI Costs
          </TabsTrigger>
          <TabsTrigger value="audit" className="rounded-sm text-xs" data-testid="tab-audit">
            <Shield className="h-3 w-3 mr-1" />Audit & Compliance
          </TabsTrigger>
          <TabsTrigger value="integrations" className="rounded-sm text-xs" data-testid="tab-integrations">
            <Wifi className="h-3 w-3 mr-1" />Integrations
          </TabsTrigger>
        </TabsList>

        {/* === PERFORMANCE TAB === */}
        <TabsContent value="performance">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-slate-800">AI Predictions</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase">Total</p>
                    <p className="font-mono text-lg font-bold">{perf?.total_predictions}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase">Avg Confidence</p>
                    <p className="font-mono text-lg font-bold">{perf?.avg_confidence}%</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase">High Confidence</p>
                    <p className="font-mono text-lg font-bold text-green-600">{perf?.high_confidence_rate}%</p>
                  </div>
                </div>
                <Separator />
                <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">By Type</p>
                {perf?.predictions_by_type?.map(p => (
                  <div key={p.type} className="flex items-center justify-between py-1">
                    <span className="text-xs text-slate-700">{p.type}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono">{p.count}</span>
                      <Badge variant="outline" className="rounded-sm text-[9px]">{p.avg_confidence}%</Badge>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-slate-800">Confidence Distribution</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {perf?.confidence_distribution?.map(d => (
                  <div key={d.bucket} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono text-slate-700">{d.bucket}%</span>
                      <span className="text-xs font-mono font-bold">{d.count}</span>
                    </div>
                    <Progress
                      value={Math.min((d.count / Math.max(perf?.total_predictions, 1)) * 100 * 3, 100)}
                      className="h-2 rounded-sm"
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* === AI COSTS TAB === */}
        <TabsContent value="costs">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-slate-800">Cost Overview</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase">Total API Spend</p>
                    <p className="font-mono text-lg font-bold">${costs?.total_api_spend?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase">Per Invoice</p>
                    <p className="font-mono text-lg font-bold">${costs?.avg_cost_per_invoice?.toFixed(4)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase">FTE Avoided</p>
                    <p className="font-mono text-lg font-bold text-green-600">${costs?.fte_cost_avoided?.toLocaleString()}</p>
                  </div>
                </div>
                <Separator />
                <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Cost by Model</p>
                {costs?.cost_by_model?.map(m => (
                  <div key={m.model} className="flex items-center justify-between py-1 border-b border-slate-50">
                    <span className="text-xs text-slate-700">{m.model}</span>
                    <div className="flex items-center gap-3 text-xs font-mono">
                      <span className="text-slate-400">{m.calls} calls</span>
                      <span className="font-bold">${m.cost.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-slate-800">Cost by Invoice (Top 20)</CardTitle></CardHeader>
              <CardContent className="max-h-80 overflow-y-auto space-y-1">
                {costs?.cost_per_invoice?.map(c => (
                  <div key={c.invoice} className="flex items-center justify-between py-1.5 border-b border-slate-50">
                    <span className="text-xs font-mono text-slate-700">{c.invoice}</span>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-slate-400">{c.calls} calls</span>
                      <span className="font-mono font-bold">${c.cost.toFixed(4)}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* === AUDIT & COMPLIANCE TAB === */}
        <TabsContent value="audit">
          <div className="space-y-4">
            {/* Hash Chain Integrity */}
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none" data-testid="chain-integrity-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Lock className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="text-sm font-bold text-slate-900">Immutable Audit Chain</p>
                      <p className="text-xs text-slate-400">{audit?.total_immutable} entries in chain</p>
                    </div>
                    {chainVerify && (
                      <Badge variant="outline" className={`rounded-sm text-[10px] ml-3 ${
                        chainVerify.valid ? 'border-green-300 text-green-700 bg-green-50' : 'border-red-300 text-red-700 bg-red-50'
                      }`} data-testid="chain-status">
                        {chainVerify.valid ? (
                          <><CheckCircle className="h-3 w-3 mr-1" />Chain Valid</>
                        ) : (
                          <><XCircle className="h-3 w-3 mr-1" />Chain Broken at #{chainVerify.broken_at_id}</>
                        )}
                      </Badge>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={verifyChain}
                    disabled={verifying}
                    data-testid="verify-chain-button"
                    className="rounded-sm text-xs"
                  >
                    {verifying ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Shield className="h-3 w-3 mr-1" />}
                    Verify Integrity
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Audit Log Explorer */}
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-800">Audit Trail Explorer</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b border-slate-100">
                        <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Time</th>
                        <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Event</th>
                        <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Entity</th>
                        <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Actor</th>
                        <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Action</th>
                        <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Hash</th>
                      </tr>
                    </thead>
                    <tbody>
                      {audit?.immutable?.map(e => (
                        <tr key={e.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`audit-row-${e.id}`}>
                          <td className="px-5 py-2 text-[10px] font-mono text-slate-400">
                            {new Date(e.created_at).toLocaleString()}
                          </td>
                          <td className="px-5 py-2">
                            <Badge variant="outline" className="rounded-sm text-[9px] uppercase tracking-wider">{e.event_type}</Badge>
                          </td>
                          <td className="px-5 py-2 text-xs text-slate-600">
                            {e.entity_type}:{e.entity_id?.slice(0, 8)}...
                          </td>
                          <td className="px-5 py-2 text-xs text-slate-600">{e.actor_name || 'System'}</td>
                          <td className="px-5 py-2 text-xs text-slate-700 max-w-[200px] truncate">{e.action}</td>
                          <td className="px-5 py-2">
                            <span className="text-[9px] font-mono text-slate-400">{e.entry_hash?.slice(0, 12)}...</span>
                          </td>
                        </tr>
                      ))}
                      {(!audit?.immutable || audit.immutable.length === 0) && (
                        <tr>
                          <td colSpan={6} className="px-5 py-12 text-center text-xs text-slate-400">
                            No immutable audit entries yet. They are created when approvals and integrations run.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Legacy Audit Log */}
            {audit?.legacy?.length > 0 && (
              <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-800">System Audit Log (Legacy)</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-white">
                        <tr className="border-b border-slate-100">
                          <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Time</th>
                          <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Action</th>
                          <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Resource</th>
                          <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">User</th>
                        </tr>
                      </thead>
                      <tbody>
                        {audit.legacy.map(e => (
                          <tr key={e.id} className="border-b border-slate-50">
                            <td className="px-5 py-2 text-[10px] font-mono text-slate-400">
                              {new Date(e.timestamp).toLocaleString()}
                            </td>
                            <td className="px-5 py-2 text-xs text-slate-700">{e.action}</td>
                            <td className="px-5 py-2 text-xs text-slate-600">{e.resource_type}:{e.resource_id?.slice(0, 8)}...</td>
                            <td className="px-5 py-2 text-xs text-slate-600">{e.user_name || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* === INTEGRATIONS TAB === */}
        <TabsContent value="integrations">
          <div className="space-y-4">
            {/* Integration Health Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {integrations.map(i => (
                <Card key={i.integration_name} className="bg-white border border-slate-200 rounded-sm shadow-none" data-testid={`integration-card-${i.integration_name}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <HealthDot status={i.status} />
                      <span className="text-sm font-bold text-slate-900 capitalize">{i.integration_name.replace(/_/g, ' ')}</span>
                      <Badge variant="outline" className={`ml-auto rounded-sm text-[10px] ${
                        i.status === 'healthy' ? 'border-green-300 text-green-700' :
                        i.status === 'degraded' ? 'border-amber-300 text-amber-700' :
                        'border-red-300 text-red-700'
                      }`}>{i.status}</Badge>
                    </div>
                    <div className="space-y-1 text-xs text-slate-500">
                      {i.last_success_at && <p>Last success: <span className="font-mono">{new Date(i.last_success_at).toLocaleString()}</span></p>}
                      {i.last_failure_at && <p className="text-red-500">Last failure: <span className="font-mono">{new Date(i.last_failure_at).toLocaleString()}</span></p>}
                      {i.consecutive_failures > 0 && <p className="text-red-600 font-medium">Consecutive failures: {i.consecutive_failures}</p>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* RightAngle Sync Log */}
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none" data-testid="ra-sync-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-semibold text-slate-800">RightAngle Sync Log</CardTitle>
                {raSync?.stats && (
                  <div className="flex items-center gap-3 text-xs text-slate-400">
                    <span>Total: <b>{raSync.stats.total_syncs}</b></span>
                    <span className="text-green-600">OK: <b>{raSync.stats.successful}</b></span>
                    <span className="text-red-500">Fail: <b>{raSync.stats.failed}</b></span>
                  </div>
                )}
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b border-slate-100">
                        <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Type</th>
                        <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Direction</th>
                        <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Records</th>
                        <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Status</th>
                        <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {raSync?.recent_syncs?.map(s => (
                        <tr key={s.id} className="border-b border-slate-50">
                          <td className="px-5 py-2 text-xs text-slate-700">{s.sync_type}</td>
                          <td className="px-5 py-2">
                            <Badge variant="outline" className="rounded-sm text-[9px]">{s.sync_direction}</Badge>
                          </td>
                          <td className="px-5 py-2 text-xs font-mono text-right">{s.records_synced}</td>
                          <td className="px-5 py-2">
                            <Badge variant="outline" className={`rounded-sm text-[9px] ${
                              s.status === 'completed' ? 'border-green-300 text-green-700' : 'border-red-300 text-red-700'
                            }`}>{s.status}</Badge>
                          </td>
                          <td className="px-5 py-2 text-[10px] font-mono text-slate-400">
                            {s.started_at ? new Date(s.started_at).toLocaleString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
